/**
 * URL utilities for Ekşi Sözlük-style routing
 * Handles Turkish character conversion, slug generation, and URL parsing
 */

/**
 * Generate URL-safe slug from text, preserving Turkish characters
 * @param text - Text to convert to slug
 * @returns URL-safe slug
 */
export function generateSlug(text: string): string {
    let slug = text.toLowerCase().trim()

    // Replace spaces and special characters with hyphens but KEEP Turkish characters (çğıöşü)
    slug = slug
        .replace(/[^a-z0-9çğışöü]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .replace(/-+/g, '-')

    return slug
}

/**
 * Generate topic URL in Ekşi Sözlük format: /slug--id
 * @param title - Topic title
 * @param id - Topic ID (can be UUID or numeric ID)
 * @param queryParams - Optional query parameters
 * @returns Formatted topic URL
 */
export function generateTopicUrl(
    title: string,
    id: string,
    queryParams?: Record<string, string | boolean | number>
): string {
    const slug = generateSlug(title)

    // If slug and ID are essentially same, use cleaner URL
    // But since our routing prefers slug--id for parsing, we keep it for now
    // but ensured TopicPage parses it correctly.
    let url = `/${slug}--${encodeURIComponent(id)}`

    if (queryParams) {
        url = addQueryParams(url, queryParams)
    }

    return url
}

/**
 * Generate entry URL: /entry/id
 * @param entryId - Entry ID
 * @param queryParams - Optional query parameters
 * @returns Formatted entry URL
 */
export function generateEntryUrl(
    entryId: string,
    queryParams?: Record<string, string | boolean | number>
): string {
    let url = `/entry/${entryId}`

    if (queryParams) {
        url = addQueryParams(url, queryParams)
    }

    return url
}

export function parseTopicSlug(slug: string): { title: string; id: string } | null {
    // Decode the slug first
    const decodedSlug = decodeURIComponent(slug)

    // Try to match the slug--id format, use non-greedy matching for the title part
    const match = decodedSlug.match(/^(.+?)--([a-f0-9-]+|\d+|.+)$/i)

    if (!match) {
        // Fallback: if no --, treat the whole slug as title/id
        const title = decodedSlug.replace(/-/g, ' ')
        return {
            title: title,
            id: title,
        }
    }

    return {
        title: match[1].replace(/-/g, ' '),
        id: match[2],
    }
}

/**
 * Add query parameters to URL
 * @param url - Base URL
 * @param params - Query parameters
 * @returns URL with query parameters
 */
export function addQueryParams(
    url: string,
    params: Record<string, string | boolean | number>
): string {
    const searchParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            searchParams.append(key, String(value))
        }
    })

    const queryString = searchParams.toString()
    return queryString ? `${url}?${queryString}` : url
}

/**
 * Validate UUID format
 * @param id - ID to validate
 * @returns True if valid UUID
 */
export function isValidUUID(id: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    return uuidRegex.test(id)
}

/**
 * Get canonical URL for a topic (without query params)
 * @param title - Topic title
 * @param id - Topic ID
 * @returns Canonical URL
 */
export function getCanonicalTopicUrl(title: string, id: string): string {
    return generateTopicUrl(title, id)
}

/**
 * Get canonical URL for an entry
 * @param entryId - Entry ID
 * @returns Canonical URL
 */
export function getCanonicalEntryUrl(entryId: string): string {
    return generateEntryUrl(entryId)
}
